import {
  ActivityIcon,
  Notice,
  SearchIcon,
  TrendingUpIcon,
} from '@buffer-mono/popcorn'

import { getNoDataDescription } from '../../utils'

import styles from './MetricsNarrativeChart.module.css'

import type { JSX } from 'react'

interface InsightNoticeProps {
  hasMeaningfulData: boolean
  isBenchmarkView: boolean
  selectedDateRange?: string
  message: string | null
  isPositive: boolean
  /** `'inactive'` renders the "no activity — review your takeaways" prompt
   *  instead of a metric observation. @see CORE-6444 */
  variant?: 'inactive'
  /** Scrolls to the Takeaways section — wired to the inactive prompt's link. */
  onReviewTakeaways?: () => void
  /** Whether the selected period includes the past week. Only a current period
   *  gets the actionable "review your takeaways" prompt with the link; a period
   *  more than a week in the past can't be acted on, so it gets a plain
   *  past-tense note. @see CORE-6444 */
  isCurrentPeriod?: boolean
}

export function InsightNotice({
  hasMeaningfulData,
  isBenchmarkView,
  selectedDateRange,
  message,
  isPositive,
  variant,
  onReviewTakeaways,
  isCurrentPeriod = true,
}: InsightNoticeProps): JSX.Element | null {
  // A flat metric on an inactive channel: nudge toward Takeaways rather than
  // observing that a zero "held steady". @see CORE-6444
  if (variant === 'inactive') {
    // A period more than a week in the past can't be acted on now (it may be 0
    // then but not today), so it gets a plain past-tense note with no link.
    if (!isCurrentPeriod) {
      return (
        <Notice
          variant="neutral"
          icon={<ActivityIcon size="small" />}
          className={styles.insightNotice}
        >
          <Notice.Text>You were not active during this period.</Notice.Text>
        </Notice>
      )
    }

    return (
      <Notice
        variant="neutral"
        icon={<ActivityIcon size="small" />}
        className={styles.insightNotice}
      >
        <Notice.Text>
          {"You haven't been active during this period. Review your "}
          {onReviewTakeaways ? (
            <button
              type="button"
              className={styles.takeawaysLink}
              onClick={onReviewTakeaways}
            >
              takeaways
            </button>
          ) : (
            'takeaways'
          )}
          {' to get started!'}
        </Notice.Text>
      </Notice>
    )
  }

  // Benchmark view always has its own message logic and is never treated as empty here.
  if (!isBenchmarkView && !hasMeaningfulData) {
    return (
      <Notice
        variant="neutral"
        icon={<SearchIcon size="small" />}
        className={styles.insightNotice}
      >
        <Notice.Text>
          {getNoDataDescription(selectedDateRange)} Try a different date range.
        </Notice.Text>
      </Notice>
    )
  }

  if (!message) return null

  return (
    <Notice
      variant="info"
      icon={
        isPositive ? (
          <TrendingUpIcon size="small" />
        ) : (
          <ActivityIcon size="small" />
        )
      }
      className={styles.insightNotice}
    >
      <Notice.Text>{message}</Notice.Text>
    </Notice>
  )
}
